import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  MapPin,
  Package,
  CreditCard,
  FileText,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Star,
  RotateCcw,
  Repeat,
} from "lucide-react";
import { Order } from "../../types/Order";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Price from "../Currency/Price";
import { ImageWithFallback } from "../../constants/image-placeholder";
import BaseApi from "../../services/base-api";
import { useToast } from "../../context/ToastContext";
import MyFatoorahPayment from "../../pages/MyFatoorahPayment";
import ProductRating from "../Product/rating";
import ConfirmModal from "../Modals/Confirm";
import ReturnRequestModal from "../Returns/ReturnRequestModal";
import {
  isActiveReturnRequest,
  ReturnsApi,
} from "../../services/returns-api";
import {
  ReturnRequest,
  ReturnRequestType,
} from "../../types/ReturnRequest";

interface OrderDetailsProps {
  order: Order;
  onBack: () => void;
  onDownloadInvoice: (orderId: string) => Promise<void>;
  onViewInvoice: (orderId: string) => Promise<void>;
  invoiceLoading: string | null;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ order, onBack }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success, err } = useToast();
  const [loadingInvoiceInfo, setLoadingInvoiceInfo] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentOrderId, setPaymentOrderId] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [showRatings, setShowRatings] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  const [cancellationRequestStatus, setCancellationRequestStatus] = useState<
    "pending" | "approved" | "rejected" | null
  >(order.cancellationRequestStatus || null);
  const [cancellationRequestedAt, setCancellationRequestedAt] = useState<
    string | null
  >(order.cancellationRequestedAt || null);
  const [cancellationReason, setCancellationReason] = useState<string | null>(
    order.cancellationReason || null,
  );
  const [cancellationReviewedAt, setCancellationReviewedAt] = useState<
    string | null
  >(order.cancellationReviewedAt || null);
  const [cancellationReviewNotes, setCancellationReviewNotes] = useState<
    string | null
  >(order.cancellationReviewNotes || null);
  const [returnPolicyDays, setReturnPolicyDays] = useState(7);
  const [activeReturnRequest, setActiveReturnRequest] =
    useState<ReturnRequest | null>(null);
  const [returnRequestLoading, setReturnRequestLoading] = useState(false);
  const [returnModalType, setReturnModalType] =
    useState<ReturnRequestType | null>(null);

  useEffect(() => {
    setCancellationRequestStatus(order.cancellationRequestStatus || null);
    setCancellationRequestedAt(order.cancellationRequestedAt || null);
    setCancellationReason(order.cancellationReason || null);
    setCancellationReviewedAt(order.cancellationReviewedAt || null);
    setCancellationReviewNotes(order.cancellationReviewNotes || null);
  }, [
    order.cancellationRequestStatus,
    order.cancellationRequestedAt,
    order.cancellationReason,
    order.cancellationReviewedAt,
    order.cancellationReviewNotes,
  ]);

  useEffect(() => {
    const fetchReturnEligibilityContext = async () => {
      try {
        setReturnRequestLoading(true);
        const [policyDays, requestsRes] = await Promise.all([
          ReturnsApi.fetchPolicyDays(),
          ReturnsApi.listMine(1, 100),
        ]);
        setReturnPolicyDays(policyDays);
        const activeRequest = (requestsRes?.data || []).find(
          (request) =>
            request.orderId === order.id &&
            isActiveReturnRequest(request.status),
        );
        setActiveReturnRequest(activeRequest || null);
      } catch (error) {
        console.error("Failed to load return eligibility", error);
      } finally {
        setReturnRequestLoading(false);
      }
    };

    fetchReturnEligibilityContext();
  }, [order.id]);

  const handleDownloadInvoice = async () => {
    try {
      setDownloadingInvoice(true);
      const res = await BaseApi.get(`/invoices/download/${order.id}`, {
        responseType: "blob",
      });

      // Create blob URL
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );

      // Create temporary link
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${order.id}.pdf`);

      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      success(t("Invoice downloaded successfully"));
    } catch (error) {
      console.error("Error downloading invoice:", error);
      err(t("Failed to download invoice"));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      setCancellingOrder(true);
      const res = await BaseApi.post(`/orders/${orderId}/cancel`, {});
      const responseOrder = res?.data;

      setCancellationRequestStatus(
        responseOrder?.cancellationRequestStatus || "pending",
      );
      setCancellationRequestedAt(
        responseOrder?.cancellationRequestedAt || new Date().toISOString(),
      );
      setCancellationReason(
        responseOrder?.cancellationReason ||
          t("Customer requested cancellation"),
      );
      setCancellationReviewedAt(responseOrder?.cancellationReviewedAt || null);
      setCancellationReviewNotes(
        responseOrder?.cancellationReviewNotes || null,
      );
      setShowCancelConfirmModal(false);
      success(t("Cancellation request submitted successfully"));
    } catch (error) {
      console.error(t("Failed to cancel order"), error);
      err(t("Failed to cancel order"));
    } finally {
      setCancellingOrder(false);
    }
  };

  const handleResumePayment = (order: Order) => {
    setPaymentOrderId(order.id);
    setPaymentAmount(Number(order.total));
    setPaymentMethod(order.paymentMethod || "card");
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    setShowPaymentModal(false);
    setPaymentOrderId(null);
    success(t("Payment completed successfully!"));
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setPaymentOrderId(null);
  };

  const handlePaymentError = (error: string) => {
    console.error("Payment error:", error);
    err(t("Payment failed. Please try again."));
  };

  const canResumePayment = (order: Order) => {
    return (
      order.paymentStatus === "pending" &&
      order.status !== "cancelled" &&
      order.status !== "delivered" &&
      cancellationRequestStatus !== "pending" &&
      cancellationRequestStatus !== "approved"
    );
  };

  const canRateOrder = (order: Order) => {
    return order.status === "delivered";
  };

  const getReturnEligibility = () => {
    if (order.status !== "delivered" || !order.deliveredAt) {
      return {
        eligible: false,
        expiryDate: null as Date | null,
        daysLeft: 0,
        reason: t("Only delivered orders are eligible"),
      };
    }

    const deliveredAt = new Date(order.deliveredAt);
    const expiryDate = new Date(deliveredAt);
    expiryDate.setDate(expiryDate.getDate() + returnPolicyDays);
    const now = new Date();
    const msLeft = expiryDate.getTime() - now.getTime();
    const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));

    if (msLeft < 0) {
      return {
        eligible: false,
        expiryDate,
        daysLeft: 0,
        reason: t("Return and exchange window expired"),
      };
    }

    if (activeReturnRequest) {
      return {
        eligible: false,
        expiryDate,
        daysLeft,
        reason: t("An active return or exchange request already exists"),
      };
    }

    return {
      eligible: true,
      expiryDate,
      daysLeft,
      reason: "",
    };
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "pending":
        return {
          icon: Clock,
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          label: t("Pending"),
        };
      case "confirmed":
        return {
          icon: CheckCircle,
          color: "bg-blue-100 text-blue-800 border-blue-200",
          label: t("Confirmed"),
        };
      case "processing":
        return {
          icon: Package,
          color: "bg-purple-100 text-purple-800 border-purple-200",
          label: t("Processing"),
        };
      case "shipped":
        return {
          icon: Package,
          color: "bg-indigo-100 text-indigo-800 border-indigo-200",
          label: t("Shipped"),
        };
      case "delivered":
        return {
          icon: CheckCircle,
          color: "bg-green-100 text-green-800 border-green-200",
          label: t("Delivered"),
        };
      case "cancelled":
        return {
          icon: XCircle,
          color: "bg-red-100 text-red-800 border-red-200",
          label: t("Cancelled"),
        };
      default:
        return {
          icon: Package,
          color: "bg-gray-100 text-gray-800 border-gray-200",
          label: status,
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateTotals = () => {
    const subtotal = order.items.reduce(
      (sum, item) => sum + Number(item.total || 0),
      0,
    );
    const shippingFee = Number(order.shippingFee || 0);
    const tax = Number(order.tax || 0);
    const discount = Number(order.discount || 0);
    const total = subtotal + shippingFee + tax - discount;

    return {
      subtotal,
      shippingFee,
      tax,
      discount,
      total,
    };
  };

  const statusConfig = getStatusConfig(order.status);
  const StatusIcon = statusConfig.icon;
  const totals = calculateTotals();
  const canRate = canRateOrder(order);
  const returnEligibility = getReturnEligibility();
  const hasPendingCancellationRequest = cancellationRequestStatus === "pending";
  const hasApprovedCancellationRequest =
    cancellationRequestStatus === "approved";
  const hasRejectedCancellationRequest =
    cancellationRequestStatus === "rejected";
  const canRequestCancellation =
    order.status !== "delivered" &&
    order.status !== "cancelled" &&
    !hasPendingCancellationRequest &&
    !hasApprovedCancellationRequest;

  return (
    <div
      className="bg-white rounded-2xl shadow-xl overflow-hidden"
      style={{ animation: "fadeIn 0.5s ease-out" }}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-black to-gray-700 p-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          {t("Back to Orders")}
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white">
              {t("Order")} #{order.orderNumber}
            </h2>
            <p className="text-gray-300 mt-1">
              {t("Placed on")} {formatDate(order.createdAt)}
            </p>
          </div>
          <span
            className={`px-4 py-2 rounded-full text-sm font-semibold border inline-flex items-center gap-2 ${statusConfig.color}`}
          >
            <StatusIcon className="w-4 h-4" />
            {statusConfig.label}
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Invoice Section */}
        {loadingInvoiceInfo ? (
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-gray-600" />
              <span className="text-gray-600">
                {t("Loading invoice information...")}
              </span>
            </div>
          </div>
        ) : order.invoice?.id ? (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-[#0d223f]">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t("Invoice")} #{order.invoice?.invoiceNumber}
                  </h3>
                  {order.invoice?.status && (
                    <div className="mt-2 space-y-1 text-sm">
                      <p className="text-gray-700">
                        <span className="font-medium">{t("Status")}:</span>{" "}
                        {order.invoice.status === "paid" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
                            <CheckCircle className="w-3 h-3" />
                            {t("Paid")}
                          </span>
                        ) : order.invoice.status === "cancelled" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-medium">
                            <XCircle className="w-3 h-3" />
                            {t("Cancelled")}
                          </span>
                        ) : order.invoice.status === "sent" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
                            <Clock className="w-3 h-3" />
                            {t("Sent")}
                          </span>
                        ) : order.invoice.status === "draft" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 font-medium">
                            <Clock className="w-3 h-3" />
                            {t("Draft")}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 font-medium">
                            {order.invoice?.status}
                          </span>
                        )}
                      </p>

                      {order.invoice?.dueDate && (
                        <p className="text-gray-700">
                          <span className="font-medium">{t("Due Date")}:</span>{" "}
                          {formatDate(order.invoice?.dueDate)}
                        </p>
                      )}
                      {order.invoice?.paidAt && (
                        <p className="text-gray-700">
                          <span className="font-medium">{t("Paid On")}:</span>{" "}
                          {formatDate(order.invoice?.paidAt)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions Section */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4">
                <div className="flex flex-wrap gap-3">
                  {/* Cancel Order - Only if allowed */}
                  {canRequestCancellation && (
                    <button
                      onClick={() => setShowCancelConfirmModal(true)}
                      className="bg-white hover:bg-gray-50 text-red-600 font-medium py-3 px-6 rounded-lg border-2 border-red-300 transition-all flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-5 h-5" />
                      {hasRejectedCancellationRequest
                        ? t("Request Cancellation Again")
                        : t("Cancel Order")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t("Invoice Not available yet")}
                  </h3>
                  <p className="text-sm text-gray-700 mt-1">
                    {order.status !== "confirmed"
                      ? t("Available once order is confirmed")
                      : t(
                          "Request to generate and download invoice for this order",
                        )}
                  </p>
                </div>
              </div>
              {order.status === "confirmed" && (
                <button
                  onClick={handleDownloadInvoice}
                  disabled={downloadingInvoice}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium disabled:opacity-50"
                >
                  {downloadingInvoice ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                  {t("Download Invoice")}
                </button>
              )}
            </div>
          </div>
        )}

        {(hasPendingCancellationRequest ||
          hasApprovedCancellationRequest ||
          hasRejectedCancellationRequest) && (
          <div
            className={`rounded-xl border p-4 ${
              hasPendingCancellationRequest
                ? "bg-amber-50 border-amber-200"
                : hasApprovedCancellationRequest
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200"
            }`}
          >
            <div className="flex items-start gap-3">
              {hasPendingCancellationRequest ? (
                <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
              ) : hasApprovedCancellationRequest ? (
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              )}

              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  {hasPendingCancellationRequest
                    ? t("Cancellation Request Pending")
                    : hasApprovedCancellationRequest
                      ? t("Cancellation Request Approved")
                      : t("Cancellation Request Rejected")}
                </h3>

                {cancellationReason && (
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{t("Reason")}:</span>{" "}
                    {cancellationReason}
                  </p>
                )}

                {cancellationRequestedAt && (
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{t("Requested On")}:</span>{" "}
                    {formatDate(cancellationRequestedAt)}
                  </p>
                )}

                {cancellationReviewedAt && (
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{t("Reviewed On")}:</span>{" "}
                    {formatDate(cancellationReviewedAt)}
                  </p>
                )}

                {cancellationReviewNotes && (
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">{t("Review Notes")}:</span>{" "}
                    {cancellationReviewNotes}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {order.status === "delivered" && (
          <div
            className={`rounded-xl border-2 p-6 ${
              returnEligibility.eligible
                ? "border-green-200 bg-green-50"
                : activeReturnRequest
                  ? "border-blue-200 bg-blue-50"
                  : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-white p-3">
                  <RotateCcw className="h-6 w-6 text-gray-800" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t("Returns and Exchanges")}
                  </h3>
                  <p className="mt-1 text-sm text-gray-700">
                    {t("Returnable/exchangeable within")} {returnPolicyDays}{" "}
                    {t("days of delivery")}
                  </p>
                  {returnEligibility.expiryDate && (
                    <p className="mt-1 text-sm text-gray-700">
                      {t("Returnable until")}{" "}
                      <span className="font-semibold">
                        {formatDate(returnEligibility.expiryDate.toISOString())}
                      </span>
                      {returnEligibility.daysLeft > 0 && (
                        <span>
                          {" "}
                          ({returnEligibility.daysLeft} {t("days left")})
                        </span>
                      )}
                    </p>
                  )}
                  {!returnEligibility.eligible && returnEligibility.reason && (
                    <p className="mt-2 text-sm font-semibold text-gray-800">
                      {returnEligibility.reason}
                    </p>
                  )}
                  {activeReturnRequest && (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/account/returns/${activeReturnRequest.id}`)
                      }
                      className="mt-3 text-sm font-semibold text-blue-800 underline"
                    >
                      {t("View active request")}
                    </button>
                  )}
                </div>
              </div>

              {returnEligibility.eligible && (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => setReturnModalType("return")}
                    disabled={returnRequestLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 font-semibold text-white transition-colors hover:bg-gray-800 disabled:opacity-60"
                  >
                    <RotateCcw className="h-5 w-5" />
                    {t("Request Return")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReturnModalType("exchange")}
                    disabled={returnRequestLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-100 px-5 py-3 font-semibold text-gray-800 transition-colors hover:bg-gray-200 disabled:opacity-60"
                  >
                    <Repeat className="h-5 w-5" />
                    {t("Request Exchange")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Payment Alert Banner */}
        {order.paymentStatus === "pending" &&
          order.status !== "cancelled" &&
          !hasPendingCancellationRequest &&
          !hasApprovedCancellationRequest && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t("Payment Pending")}
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    {t(
                      "Your order is waiting for payment. Complete the payment to proceed with your order.",
                    )}
                  </p>
                  <button
                    onClick={() => handleResumePayment(order)}
                    className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium py-2 px-4 rounded-md transition-all"
                  >
                    {t("Complete Payment Now")}
                  </button>
                </div>
              </div>
            </div>
          )}

        {/* Rate Products Banner - Show for delivered orders */}
        {canRate && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-start gap-3">
                <div className="bg-white p-3 rounded-lg">
                  <Star className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t("Rate Your Purchase")}
                  </h3>
                  <p className="text-sm text-gray-700 mt-1">
                    {t(
                      "Share your experience and help other customers make informed decisions",
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRatings(!showRatings)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
              >
                <Star className="w-5 h-5" />
                {showRatings ? t("Hide Ratings") : t("Rate Products")}
              </button>
            </div>
          </div>
        )}

        {/* Shipping Address and Payment Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Shipping Address */}
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t("Shipping Address")}
            </h3>
            <div className="space-y-1 text-gray-700">
              <p className="font-semibold">{order.shippingAddress.fullName}</p>
              <p>{order.shippingAddress.street}</p>
              {order.shippingAddress.apartment && (
                <p>{order.shippingAddress.apartment}</p>
              )}
              <p>
                {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                {order.shippingAddress.zipCode}
              </p>
              <p>{order.shippingAddress.country}</p>
              {order.shippingAddress.phone && (
                <p className="mt-2 font-medium">
                  {order.shippingAddress.phone}
                </p>
              )}
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {t("Payment Information")}
              </h3>

              {/* Payment Status Badge */}
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  order.paymentStatus === "paid"
                    ? "bg-green-100 text-green-800"
                    : order.paymentStatus === "pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                }`}
              >
                {order.paymentStatus?.toUpperCase()}
              </span>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t("Payment Method")}:</span>
                <span className="font-medium capitalize">
                  {order.paymentMethod || "Card"}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t("Payment Status")}:</span>
                <span
                  className={`font-medium ${
                    order.paymentStatus === "paid"
                      ? "text-green-600"
                      : "text-yellow-600"
                  }`}
                >
                  {order.paymentStatus}
                </span>
              </div>

              {/* Resume Payment Button */}
              {canResumePayment(order) && (
                <div className="mt-4 pt-4 border-t border-gray-600">
                  <button
                    onClick={() => handleResumePayment(order)}
                    className="w-full bg-gradient-to-r from-gray-700 to-black hover:from-gray-700 hover:to-black text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                  >
                    <CreditCard className="w-5 h-5" />
                    {t("Resume Payment")}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    {t("Complete your payment to process your order")}
                  </p>
                </div>
              )}

              {/* Payment Completed Info */}
              {order.paymentStatus === "paid" && order.updatedAt && (
                <div className="mt-4 pt-4 border-t border-gray-600">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <div>
                      <p className="text-sm font-medium">
                        {t("Payment Completed")}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.updatedAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Order Items with Rating */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5" />
            {t("Order Items")}
          </h3>
          <div className="space-y-4">
            {order.items.map((item) => (
              <div
                key={item.id}
                className="bg-gray-50 rounded-xl overflow-hidden"
              >
                {/* Product Info */}
                <div className="flex items-center gap-4 p-4">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-lg overflow-hidden">
                    <ImageWithFallback
                      src={item.product?.images?.[0]}
                      alt={item.productName.en}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-base sm:text-lg truncate">
                      {item.productName.en}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {t("Quantity")}: {item.quantity}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t("Unit Price")}: <Price amount={item.unitPrice} />
                    </p>
                  </div>
                  <div className="font-bold text-gray-900 text-lg">
                    <Price amount={item.total} />
                  </div>
                </div>

                {/* Rating Section - Show for delivered orders */}
                {showRatings && canRate && (
                  <div className="border-t-2 border-gray-200 p-4 bg-white">
                    <ProductRating
                      product={{
                        id: item.productId,
                        name: item.productName,
                        rating: item.product?.rating,
                        ratingCount: item.product?.ratingCount || 0,
                      }}
                      orderId={order.id}
                      showReviews={false}
                      allowRating={true}
                      mode="full"
                      size="md"
                      showCount={false}
                      onRatingUpdate={(rating) => {
                        console.info("Product rated:", item.productId, rating);
                        // You can add additional logic here if needed
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {t("Order Summary")}
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between text-gray-700">
              <span>{t("Subtotal")}:</span>
              <span className="font-semibold">
                <Price amount={totals.subtotal} />
              </span>
            </div>
            {totals.tax > 0 && (
              <div className="flex justify-between text-gray-700">
                <span>{t("Tax")}:</span>
                <span className="font-semibold">
                  <Price amount={totals.tax} />
                </span>
              </div>
            )}
            {totals.discount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>{t("Discount")}:</span>
                <span className="font-semibold">
                  -<Price amount={totals.discount} />
                </span>
              </div>
            )}
            <div className="flex justify-between text-gray-700">
              <span>{t("Shipping Fee")}:</span>
              <span className="font-semibold">
                {totals.shippingFee === 0 ? (
                  <span className="text-green-600">{t("FREE")}</span>
                ) : (
                  <Price amount={totals.shippingFee} />
                )}
              </span>
            </div>
            <div className="pt-3 border-t-2 border-gray-300 flex justify-between text-xl font-bold text-gray-900">
              <span>{t("Total")}:</span>
              <span>
                <Price amount={totals.total} />
              </span>
            </div>
          </div>
        </div>

        {/* Customer Notes */}
        {order.customerNotes && (
          <div className="bg-[#0d223f] rounded-xl p-6 border border-[#0d223f]">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {t("Your Notes")}
            </h3>
            <p className="text-gray-700">{order.customerNotes}</p>
          </div>
        )}
      </div>

      {/* MyFatoorah Payment Modal */}
      {showPaymentModal && paymentOrderId && (
        <MyFatoorahPayment
          orderId={paymentOrderId}
          amount={paymentAmount}
          paymentMethod={paymentMethod}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
          onError={handlePaymentError}
        />
      )}

      {returnModalType && (
        <ReturnRequestModal
          order={order}
          type={returnModalType}
          returnPolicyDays={returnPolicyDays}
          onClose={() => setReturnModalType(null)}
          onCreated={(requestId) => {
            setReturnModalType(null);
            navigate(`/account/returns/${requestId}`);
          }}
        />
      )}

      <ConfirmModal
        isOpen={showCancelConfirmModal}
        title={t("Request Cancellation")}
        message={t(
          "Are you sure you want to submit a cancellation request for this order?",
        )}
        confirmText={
          cancellingOrder ? t("Submitting...") : t("Request Cancellation")
        }
        cancelText={t("Keep Order")}
        icon={AlertCircle}
        iconColor="text-red-600"
        iconBg="bg-red-100"
        onConfirm={() => handleCancelOrder(order.id)}
        onCancel={() => {
          if (!cancellingOrder) {
            setShowCancelConfirmModal(false);
          }
        }}
      />
    </div>
  );
};

export default OrderDetails;
